import { useContext, useEffect, useRef, useState } from "react";
import UIContext from "components/Utils/UIContext";
import Subtitles from "./Subtitles";

import "./Tutorial.sass"
import { Button, Modal } from "antd";
import { compact, flatten, set } from "lodash";
import { useTranslation } from 'react-i18next';

// Import the SRT files
// @ts-ignore
import stakes from "./stakes.srt";
// @ts-ignore
import stakeholders from "./stakeholders.srt";
// @ts-ignore
import risks from "./risks.srt";
import { parseSRT } from "utils/subtitles";

const scripts: Record<string, any> = {
    stakes,
    stakeholders,
    risks
}

// Base des assets tutoriels (audio + images). Historiquement codée en dur sur
// `app.gerositus.com` (prod), ce qui faisait charger TOUS les environnements
// depuis la prod. Les assets sont déployés sur chaque domaine d'env
// (app.dev / app.test / app.gerositus.com), donc on prend l'origine courante.
// Repli sur la prod en dev local (localhost), qui n'héberge pas /tutorials.
const TUTORIAL_BASE = (() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (!origin || /localhost|127\.0\.0\.1/.test(origin)) return 'https://app.gerositus.com';
    return origin;
})();

// Réécrit une URL d'asset tutoriel codée en dur sur la prod vers l'origine de
// l'env courant. No-op si l'URL ne pointe pas sur le host prod.
const rewriteTutorialUrl = (url: string) =>
    url.replace(/^https?:\/\/app\.gerositus\.com/, TUTORIAL_BASE);

interface ActionDetails {
    [key: string]: any;
    originId?: string;
    stakeholderId?: string;
    riskId?: string;
    indicatorId?: string;
    type?: string;
    target?: string;
    delay?: number;
    content?: string;
    style?: string;
    initial?: string;
    final?: string;
    url?: string;
    backgroundColor?: string;
}

interface Action {
    name: string;
    details: ActionDetails;
}

interface ImageItem {
    url: string;
    details: {
        className?: string;
        animation?: string;
        duration?: string;
        [key: string]: any;
    };
}

interface TextItem {
    content: string;
    details: {
        className?: string;
        animation?: string;
        duration?: string;
        style?: any;
        backgroundColor?: string;
        [key: string]: any;
    };
}

interface SubtitleItem {
    startTime: number;
    endTime: number;
    text: string;
}

interface TutorialProps {
    type: string;
    projectId: string;
    executeAction: (id: string, name: string, details: any) => void;
    context: any;
    className?: string;
}

export default ({ type, projectId, executeAction, context, className }: TutorialProps) => {
    const { t } = useTranslation();
    // Un lecteur (projet partagé) ne fait pas de tutoriel (ils pilotent des
    // actions d'édition) ; le badge « Lecture seule » prend sa place dans le
    // sous-header. On sort avant tout hook conditionnel-free.
    const { isReadOnly } = useContext(UIContext) as any;
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioTime, setAudioTime] = useState(0);
    const [images, setImages] = useState<ImageItem[]>([]);
    const [texts, setTexts] = useState<TextItem[]>([]);
    const [imagesAreLoaded, setImagesAreLoaded] = useState(false);
    const [subtitles, setSubtitles] = useState<SubtitleItem[]>([]);

    const audio = useRef<HTMLAudioElement>(null);

    const applyActions = (actions: Action[]) => {
        actions.forEach(applyAction)
    }

    const removeImagesButLast = () => {
        if (images.length > 1) {
            console.log("removing images but last");
            setImages([...images.slice(1)])
        }
    }

    const removeAllImages = () => {
        setImages([]);
    }

    useEffect(() => {
        setTimeout(() => {
            removeImagesButLast()
        }, 2500)
    }, [images])

    const confirmExit = () => {
        if (isPlaying) {
            context.pushModal(
                <Modal
                    title={t('messages.tutorial.exit_title')}
                    onOk={() => {

                        context.popModal()
                        tearDownTutorial();
                        setTimeout(() => setIsPlaying(false), 100)
                    }}
                    onCancel={() => context.popModal()}
                >{t('messages.tutorial.exit_confirm')}</Modal >
            )
        }
    }

    const loadAllImages = (currentSubtitles: SubtitleItem[]) => {
        if (imagesAreLoaded) return;

        let allImages = listImagesInScript(currentSubtitles);

        allImages.forEach((image) => {
            const img = new Image();
            img.src = image;
        })

        return Promise.all(allImages.map((image) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = image;
                img.onload = resolve;
            })
        }))
    }

    const listImagesInScript = (currentSubtitles: SubtitleItem[]) => {
        if (!currentSubtitles?.length) return [];

        const images = compact(flatten(currentSubtitles.map(s => {
            if (!s) return null;
            const matches = s.text.match(/\[image url="([^"]+)"/);
            return matches?.[1] ? rewriteTutorialUrl(matches[1]) : null;
        })));

        return images;
    }

    const prepareTutorial = async () => {
        if (scripts[type]) {
            const parsedSubtitles = parseSRT(scripts[type]);
            setSubtitles(parsedSubtitles);

            await loadAllImages(parsedSubtitles);
        }

        executeAction(projectId, "clear", {});

        context.setMaskedZones({
            "header": true,
            "sidemenu": true,
            "summary": true,
            "add": true,
            "main": true,
        });
        if (!context?.areAllLinksVisible && document.querySelector("#SideMenu .EditableListItem .Weight")) {
            const element = document.querySelector("#SideMenu .EditableListItem .Weight");
            if (element instanceof HTMLElement) {
                element.click();
            }
        }
    }

    const tearDownTutorial = () => {
        context.deselect();
        audio.current?.pause();
        context.setMaskedZones({});
        if (audio.current) audio.current.currentTime = 0;
        executeAction(projectId, "reset", {});
        setImages([]);
        setIsPlaying(false);
    }


    const applyAction = async (action: Action) => {

        console.log("action", action);

        // Route les URLs d'assets (images) vers l'origine de l'env courant.
        if (action.details?.url) {
            action.details.url = rewriteTutorialUrl(action.details.url);
        }

        if (action.name == "click") {
            const mouseClick = new MouseEvent('click', {
                'view': window,
                'bubbles': true,
                'cancelable': true
            });

            console.log("action.details.target", action.details.target)
            console.log(document.querySelector(action.details.target || ""));

            if (action.details?.target && document.querySelector(action.details.target)) {
                const element = document.querySelector(action.details.target);
                if (element) {
                    element.dispatchEvent(mouseClick);
                }
            }

        } else if (action.name == "hover") {


            if (action.details?.delay) {
                setTimeout(() => {
                    const mouseoverEvent = new MouseEvent('mouseover', {
                        'view': window,
                        'bubbles': true,
                        'cancelable': true
                    });
                    if (action.details?.target && document.querySelector(action.details.target)) {
                        const element = document.querySelector(action.details.target);
                        if (element) {
                            element.dispatchEvent(mouseoverEvent);
                        }
                    }
                }, action.details.delay)
            } else {
                const mouseoverEvent = new MouseEvent('mouseover', {
                    'view': window,
                    'bubbles': true,
                    'cancelable': true
                });
                if (action.details?.target && document.querySelector(action.details.target)) {
                    const element = document.querySelector(action.details.target);
                    if (element) {
                        element.dispatchEvent(mouseoverEvent);
                    }
                }
            }

        } else if (action.name == "blur") {


            if (action.details?.delay) {
                setTimeout(() => {
                    const mouseoutEvent = new MouseEvent('mouseout', {
                        'view': window,
                        'bubbles': true,
                        'cancelable': true
                    });

                    if (action.details?.target && document.querySelector(action.details.target)) {
                        const element = document.querySelector(action.details.target);
                        if (element) {
                            element.dispatchEvent(mouseoutEvent);
                        }
                    }

                }, action.details.delay)
            } else {
                const mouseoutEvent = new MouseEvent('mouseout', {
                    'view': window,
                    'bubbles': true,
                    'cancelable': true
                });

                if (action.details?.target && document.querySelector(action.details.target)) {
                    const element = document.querySelector(action.details.target);
                    if (element) {
                        element.dispatchEvent(mouseoutEvent);
                    }
                }
            }

        } else if (action.name == "select") {
            // Find the item
            if (action.details.type) {
                const typeKey = action.details.type.toLowerCase() + "s";
                const item = context[typeKey]?.find((i: any) => i.id == action.details.id);
                if (item) {
                    context.select(item);
                }
            }

        } else if (action.name == "deselect") {
            context.deselect()

        } else if (action.name == "unmask") {
            let oldMaskedZones = context.maskedZones;
            if (action.details.target) {
                delete oldMaskedZones[action.details.target];
                context.setMaskedZones({
                    ...context.maskedZones
                });
            }

        } else if (action.name == "mask") {
            let oldMaskedZones = context.maskedZones;
            if (action.details.target) {
                context.setMaskedZones({
                    ...context.maskedZones,
                    [action.details.target]: true
                });
            }

        } else if (action.name == "text") {


            if (action.details.content) {
                let preparedStyle = (action.details.style || "").split(";").map((i: string) => i.split(":")).reduce((acc: any, i: string[]) => {
                    acc[i[0]] = ((i[1] + ""));
                    return acc;
                }, {})
                delete preparedStyle[""]



                const currentTexts = [...texts];
                setTimeout(() => {
                    setTexts([...currentTexts, {
                        content: action.details.content || "",
                        details: {
                            ...action.details,
                            style: {
                                ...preparedStyle,
                                opacity: 0
                            },
                            animation: action.details?.initial || "scale(1) transform(0, 0)",
                        }
                    }])
                }, action.details?.delay || 0.1)

                setTimeout(() => {
                    setTexts([...currentTexts.slice(0, -1), {
                        content: action.details.content || "",
                        details: {
                            className: "appear",
                            ...action.details,
                            style: preparedStyle,
                            animation: action.details?.final || "",
                        }
                    }])
                }, action.details?.delay ? parseInt(action.details?.delay + "") + 10 : 10)
            } else {
                setTexts([{
                    content: texts[texts.length - 1].content,
                    details: {
                        ...texts[texts.length - 1].details,
                        className: "fade",
                    }
                }])
                setTimeout(() => { setTexts([]); console.log("removed") }, 2000)
            }

        } else if (action.name == "image") {
            if (action.details.url && (images.length == 0 || images[images.length - 1].url != action.details.url)) {
                const currentImages = [...images];
                console.log("adding image", action.details.url);
                setImages([...currentImages, {
                    url: action.details.url,
                    details: {
                        ...action.details,
                        animation: action.details.initial || "scale(1) transform(0, 0)",
                    }
                }])

                setTimeout(() => {
                    console.log("appearing image");
                    if (action.details.url) {
                        setImages([...currentImages, {
                            url: action.details.url,
                            details: {
                                className: "appear",
                                ...action.details,
                                animation: action.details.final || "",
                            }
                        }]);
                    }
                }, 10)

            } else if (images.length > 0) {
                console.log("removing image", [{
                    url: images[images.length - 1].url,
                    details: {
                        ...images[images.length - 1].details,
                        className: "fade",
                    }
                }]);
                setImages([{
                    url: images[images.length - 1].url,
                    details: {
                        ...images[images.length - 1].details,
                        className: "fade",
                    }
                }])
                setTimeout(() => removeAllImages(), 2000)
            }

        } else {
            // Fix: Only execute action once, either with delay or immediately
            if (action.details?.delay) {
                // Execute with delay
                const actionParams = { ...action.details };
                // Remove delay to prevent future duplicate executions
                delete actionParams.delay;

                setTimeout(() => {
                    executeAction(
                        actionParams?.originId || actionParams?.stakeholderId || actionParams?.riskId || actionParams?.indicatorId || projectId,
                        action.name,
                        actionParams
                    );
                }, action.details.delay);
            } else {
                // Execute immediately
                executeAction(
                    action.details?.originId || action.details?.stakeholderId || action.details?.riskId || action.details?.indicatorId || projectId,
                    action.name,
                    action.details
                );
            }
        }
    }

    const onClick = async () => {
        if (!isPlaying) {
            await prepareTutorial();

            if (audio.current) audio.current.currentTime = 0;
            // Change speed
            //audio.current.playbackRate = 2;

            audio.current?.play();
            setIsPlaying(!isPlaying)
        } else {
            confirmExit();
        }
    }

    useEffect(() => {
        const audioCurrent = audio.current;
        if (audioCurrent) {
            const handleTimeUpdate = () => setAudioTime(audioCurrent.currentTime);
            audioCurrent.addEventListener('timeupdate', handleTimeUpdate);

            audioCurrent.addEventListener('ended', tearDownTutorial);
            return () => {
                audioCurrent.removeEventListener('timeupdate', handleTimeUpdate);
                audioCurrent.removeEventListener('ended', tearDownTutorial);
            }
        }
    }, []);

    // Après tous les hooks : masqué en lecture seule (le badge le remplace).
    if (isReadOnly) return null;

    return <div id="Tutorial" className={
        isPlaying ? "playing" : ""
    }>
        {isPlaying && <Subtitles callActions={applyActions} time={audioTime} start={isPlaying} url={type} subtitles={subtitles}></Subtitles>}

        <Button className={"button " + className} onClick={onClick}>
			{isPlaying ? t('messages.tutorial.exit_title') : t('messages.tutorial.button')}
		</Button>
        <audio style={{ flexShrink: 0 }} ref={audio} src={TUTORIAL_BASE + "/tutorials/" + type + "-intro/audio.mp4"} />

        {isPlaying && texts ? texts.map((i, index) => < div key={index} className={`text-backdrop ${i.details?.className}`} style={{
            backgroundColor: i.details?.backgroundColor || "rgba(0,0,0, 0.2)"
        }}>
            <div className="text" style={{
                ...(i.details?.animation ? { transform: i.details?.animation, transitionDuration: i.details?.duration } : {}),
                ...(i.details?.style || {})
            }}>{i.content}</div>
        </div>) : null
        }

        {
            isPlaying && images ? images.map((i, index) => <div key={i.url} className={`image ${i.details?.className}`}>
                <img key={i.url} src={i.url} style={i.details?.animation ? { transform: i.details?.animation, transitionDuration: i.details?.duration } : {}} />
            </div>) : null
        }
    </div >

}